import React from "react";
import { Sparkles, Code2, Search, ListChecks, FlaskConical } from "lucide-react";

/**
 * Orbit-Visualisierung: Kenny in der Mitte, 5 KI-Agenten kreisen um ihn.
 * Pulse-Ring im Zentrum, gestrichelte Verbindungslinien (animiert),
 * langsam rotierender Orbit-Ring.
 */
const agents = [
  { id: "creative", label: "Creative", desc: "Bild- & Video-Generation", icon: Sparkles },
  { id: "coding",   label: "Coding",   desc: "Tools, Automation, APIs",  icon: Code2 },
  { id: "research", label: "Research", desc: "Marktdaten & Trends",      icon: Search },
  { id: "planning", label: "Planning", desc: "Kampagnen, Drehpläne",     icon: ListChecks },
  { id: "testing",  label: "Testing",  desc: "QA & Iteration",           icon: FlaskConical },
];

const ORBIT_RADIUS = 220; // Desktop
const ORBIT_RADIUS_MOBILE = 140;

const AgentsOrbit = () => {
  return (
    <section
      className="py-16 sm:py-24 bg-secondary/20 overflow-hidden relative"
      data-testid="agents-orbit-section"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))] text-xs font-semibold uppercase tracking-widest mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Mein KI-Team
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold mb-4">
            Kenny + 5 KI-Agenten
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Ich arbeite nicht allein — ein Team aus 5 spezialisierten KI-Agenten unterstützt mich
            bei Recherche, Planung, Content-Generation, Coding und Qualitätssicherung. So liefere
            ich Marketing-Content in einer Tiefe und Geschwindigkeit, die normalerweise nur
            Agenturen schaffen.
          </p>
        </div>

        {/* Orbit container */}
        <div className="relative mx-auto w-full max-w-[640px] aspect-square">
          {/* Outer rotating orbit ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="rounded-full border border-[hsl(var(--accent))]/15"
              style={{
                width: "85%",
                height: "85%",
                animation: "orbit-rotate 60s linear infinite",
              }}
            >
              {/* Tiny dots traveling along the ring */}
              {[0, 0.5].map((offset) => (
                <div
                  key={offset}
                  className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-[hsl(var(--accent))]"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${offset * 360}deg) translateY(-${ORBIT_RADIUS}px) hidden md:block`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Connection lines (SVG) */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="-300 -300 600 600"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <radialGradient id="lineGrad" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="hsl(280, 75%, 60%)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="hsl(280, 75%, 60%)" stopOpacity="0.05" />
              </radialGradient>
            </defs>
            {agents.map((a, i) => {
              const angle = (i / agents.length) * Math.PI * 2 - Math.PI / 2;
              const x = Math.cos(angle) * ORBIT_RADIUS;
              const y = Math.sin(angle) * ORBIT_RADIUS;
              return (
                <line
                  key={a.id}
                  x1="0"
                  y1="0"
                  x2={x}
                  y2={y}
                  stroke="url(#lineGrad)"
                  strokeWidth="1.5"
                  strokeDasharray="3 6"
                  opacity="0.7"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    values="0;-18"
                    dur="1.6s"
                    repeatCount="indefinite"
                  />
                </line>
              );
            })}
          </svg>

          {/* Center: Kenny */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            {/* Pulse rings */}
            <div className="absolute inset-0 rounded-full border-2 border-[hsl(var(--accent))]/40"
                 style={{ animation: "agent-pulse 2.4s ease-out infinite" }} />
            <div className="absolute inset-0 rounded-full border-2 border-[hsl(var(--accent))]/30"
                 style={{ animation: "agent-pulse 2.4s ease-out 0.8s infinite" }} />

            <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-[hsl(var(--accent))] to-purple-700 flex items-center justify-center shadow-2xl shadow-[hsl(var(--accent))]/40 border-4 border-background overflow-hidden">
              <img
                src="https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/0uxu2fq0-kenny-profilbild.jpg"
                alt="Kenny Frey"
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-xs font-bold whitespace-nowrap bg-[hsl(var(--accent))] text-white px-3 py-1 rounded-full">
              Kenny
            </div>
          </div>

          {/* Agents positioned around */}
          {agents.map((a, i) => {
            const angle = (i / agents.length) * 360 - 90;
            const Icon = a.icon;
            return (
              <div
                key={a.id}
                className="absolute top-1/2 left-1/2 z-10"
                style={{
                  transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(0) translateY(calc(-1 * var(--orbit-r))) rotate(${-angle}deg)`,
                  ['--orbit-r']: `${ORBIT_RADIUS}px`,
                }}
                data-testid={`agent-${a.id}`}
              >
                <div className="group flex flex-col items-center">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-2xl bg-[hsl(var(--accent))]/30 blur-xl group-hover:bg-[hsl(var(--accent))]/60 transition-colors" />
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-background/90 backdrop-blur-sm border border-[hsl(var(--accent))]/40 flex items-center justify-center group-hover:border-[hsl(var(--accent))] group-hover:scale-110 transition-all">
                      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-[hsl(var(--accent))]" />
                    </div>
                  </div>
                  <div className="mt-2 text-center">
                    <div className="text-xs sm:text-sm font-semibold">{a.label}</div>
                    <div className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block max-w-[120px]">
                      {a.desc}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile fallback: switch to smaller radius via inline style on small screens */}
        <style>{`
          @keyframes orbit-rotate {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes agent-pulse {
            0%   { transform: scale(1);   opacity: 0.7; }
            100% { transform: scale(2.4); opacity: 0;   }
          }
          @media (max-width: 640px) {
            [data-testid="agents-orbit-section"] [style*="--orbit-r"] {
              --orbit-r: ${ORBIT_RADIUS_MOBILE}px !important;
            }
          }
        `}</style>

        <div className="text-center mt-16 max-w-2xl mx-auto">
          <p className="text-sm text-muted-foreground">
            Jeder Agent ist auf eine Disziplin spezialisiert und arbeitet auf
            <span className="text-[hsl(var(--accent))] font-semibold"> Freycreate.ai</span> mit den
            anderen zusammen — orchestriert von mir.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AgentsOrbit;
